/**
 * @file Plugin + module auto-discovery
 *
 * @description
 * Auto-discovers plugins from package.json dependencies and
 * modules from conventional `src/modules/` directory.
 *
 * **Plugin discovery:** scans dependencies/devDependencies for packages
 * matching `nexus-plugin-*`, imports them, and extracts the PluginManifest
 * via default export or named export (duck-typing).
 *
 * **Module discovery:** scans `src/modules/` (or `MODULES_DIR` env var)
 * for subdirectories with `index.{js,ts}`, imports them, and extracts
 * ModuleManifest exports via duck-typing.
 *
 * Factory plugins (that require runtime options) are not auto-discovered
 * and must be passed to `start()` programmatically.
 *
 * @example
 * ```typescript
 * // No config file needed!
 * // Plugins: auto-discovered from package.json
 * // Modules: auto-discovered from src/modules/
 * // Factory plugins: passed to start()
 * import { start } from '@gzl10/nexus-backend'
 *
 * await start({ plugins: [myFactoryPlugin({ ...opts })] })
 * ```
 */
import { join } from 'path'
import { existsSync, readFileSync, readdirSync, statSync } from 'fs'
import { pathToFileURL } from 'url'
import { getProjectPath } from './paths.js'
import { logger } from '@kernel/logger/index.js'
import type { PluginManifest, ModuleManifest } from '@gzl10/nexus-sdk'
import type { NexusConfig } from './types.js'

/** Pattern to match plugin package names */
const PLUGIN_PATTERN = /nexus-plugin-/

let _cache: NexusConfig | null = null

// ─── Plugin Discovery ────────────────────────────────────────────────

/**
 * Checks if a value structurally matches PluginManifest (duck-typing).
 * Rejects functions (factory plugins), null, and primitives.
 */
export function isPluginManifest(value: unknown): value is PluginManifest {
  if (!value || typeof value !== 'object' || typeof value === 'function') return false
  const v = value as Record<string, unknown>
  return (
    typeof v['name'] === 'string' &&
    Array.isArray(v['modules']) &&
    typeof v['code'] === 'string' &&
    typeof v['version'] === 'string'
  )
}

/**
 * Extract PluginManifest from a dynamically imported module.
 * Checks default export first, then named exports.
 */
export function extractPluginManifest(mod: Record<string, unknown>): PluginManifest | null {
  if (isPluginManifest(mod['default'])) return mod['default']
  for (const [key, value] of Object.entries(mod)) {
    if (key === 'default') continue
    if (isPluginManifest(value)) return value
  }
  return null
}

/**
 * Resolve the entry point of a plugin package from node_modules.
 * Handles ESM-only packages (exports with only "import" condition)
 * which createRequire().resolve() cannot handle.
 */
function resolvePluginEntry(projectPath: string, pkgName: string): string | null {
  const pkgDir = join(projectPath, 'node_modules', pkgName)
  const pkgJsonPath = join(pkgDir, 'package.json')

  if (!existsSync(pkgJsonPath)) return null

  try {
    const pluginPkg = JSON.parse(readFileSync(pkgJsonPath, 'utf-8'))
    const exports = pluginPkg.exports

    // Resolve entry: exports.'.'.import > main > module > index.js
    const importEntry = exports?.['.']?.import
    const entry =
      (typeof importEntry === 'string' ? importEntry : importEntry?.default) ??
      (typeof exports === 'string' ? exports : null) ??
      pluginPkg.main ??
      pluginPkg.module ??
      'index.js'

    const entryPath = join(pkgDir, entry)
    return existsSync(entryPath) ? entryPath : null
  } catch {
    return null
  }
}

/**
 * Discover plugins from package.json dependencies.
 * Scans dependencies and devDependencies for packages matching `nexus-plugin-*`.
 * Dynamically imports each and extracts the PluginManifest.
 *
 * Errors are logged as warnings and skipped (non-fatal).
 */
export async function discoverPlugins(projectPath: string): Promise<PluginManifest[]> {
  const pkgPath = join(projectPath, 'package.json')

  let pkg: Record<string, Record<string, string> | undefined>
  try {
    pkg = JSON.parse(readFileSync(pkgPath, 'utf-8'))
  } catch {
    return []
  }

  const allDeps = {
    ...pkg['dependencies'],
    ...pkg['devDependencies'],
  }

  const pluginNames = Object.keys(allDeps).filter((name) => PLUGIN_PATTERN.test(name))
  if (pluginNames.length === 0) return []

  const discovered: PluginManifest[] = []

  for (const pkgName of pluginNames) {
    try {
      const resolved = resolvePluginEntry(projectPath, pkgName)
      if (!resolved) {
        logger.debug({ plugin: pkgName }, 'Plugin discovery: not found in node_modules, skipping')
        continue
      }
      const mod = await import(pathToFileURL(resolved).href)
      const manifest = extractPluginManifest(mod)

      if (manifest) {
        // Default migrationsDir: <plugin>/migrations/ if it exists
        if (!manifest.migrationsDir) {
          const defaultMigrationsDir = join(projectPath, 'node_modules', pkgName, 'migrations')
          if (existsSync(defaultMigrationsDir)) {
            manifest.migrationsDir = defaultMigrationsDir
          }
        }
        // Auto-detect image: <plugin>/image.png if it exists
        if (!manifest.image) {
          const imagePath = join(projectPath, 'node_modules', pkgName, 'image.png')
          if (existsSync(imagePath)) {
            manifest.image = imagePath
          }
        }
        // Auto-detect llms.txt: <plugin>/llms.txt if it exists
        if (!manifest.llms) {
          const llmsPath = join(projectPath, 'node_modules', pkgName, 'llms.txt')
          if (existsSync(llmsPath)) {
            manifest.llms = readFileSync(llmsPath, 'utf-8')
          }
        }
        discovered.push(manifest)
      } else {
        logger.warn({ plugin: pkgName }, 'Plugin discovery: no PluginManifest export, skipping')
      }
    } catch (err) {
      const errorMsg = (err as Error).message
      logger.warn({ plugin: pkgName, err: errorMsg }, 'Plugin discovery: failed to import')
      console.error(`  ⚠ Plugin '${pkgName}' failed to load: ${errorMsg}`)
    }
  }

  return discovered
}

// ─── Module Discovery ────────────────────────────────────────────────

/**
 * Checks if a value structurally matches ModuleManifest (duck-typing).
 * Required fields: name (string) and category (string).
 * Excludes PluginManifest (which has code + modules[]).
 */
export function isModuleManifest(value: unknown): value is ModuleManifest {
  if (!value || typeof value !== 'object' || typeof value === 'function') return false
  const v = value as Record<string, unknown>
  return (
    typeof v['name'] === 'string' &&
    typeof v['category'] === 'string' &&
    !(typeof v['code'] === 'string' && Array.isArray(v['modules']))
  )
}

/**
 * Extract all ModuleManifest exports from a dynamically imported module.
 * A single index.ts can export multiple manifests (e.g. postsModule + commentsModule).
 */
export function extractModuleManifests(mod: Record<string, unknown>): ModuleManifest[] {
  const manifests: ModuleManifest[] = []

  // Check default export first
  if (isModuleManifest(mod['default'])) {
    manifests.push(mod['default'])
  }

  // Check all named exports
  for (const [key, value] of Object.entries(mod)) {
    if (key === 'default') continue
    if (isModuleManifest(value)) {
      // Avoid duplicates (same object exported as default + named)
      if (!manifests.some((m) => m.name === (value as ModuleManifest).name)) {
        manifests.push(value)
      }
    }
  }

  return manifests
}

/**
 * Discover modules from conventional directory structure.
 * Scans `src/modules/` (or `MODULES_DIR` env var) for subdirectories
 * containing `index.js` or `index.ts` that export ModuleManifest.
 *
 * Errors are logged as warnings and skipped (non-fatal).
 */
export async function discoverModules(projectPath: string): Promise<ModuleManifest[]> {
  const modulesDir = process.env['MODULES_DIR'] || join(projectPath, 'src', 'modules')

  if (!existsSync(modulesDir)) return []

  let entries: string[]
  try {
    entries = readdirSync(modulesDir)
  } catch {
    return []
  }

  const discovered: ModuleManifest[] = []

  for (const entry of entries) {
    const entryPath = join(modulesDir, entry)

    // Only process directories
    try {
      if (!statSync(entryPath).isDirectory()) continue
    } catch {
      continue
    }

    // Look for index.js or index.ts
    let indexPath: string | null = null
    for (const ext of ['index.js', 'index.ts']) {
      const candidate = join(entryPath, ext)
      if (existsSync(candidate)) {
        indexPath = candidate
        break
      }
    }

    if (!indexPath) continue

    try {
      let mod: Record<string, unknown>

      if (indexPath.endsWith('.ts')) {
        // Use tsx to import TypeScript files when running from compiled CLI
        try {
          const { tsImport } = await import('tsx/esm/api')
          mod = await tsImport(pathToFileURL(indexPath).href, import.meta.url) as Record<string, unknown>
        } catch {
          // tsx not available — fall back to native import (works when running via tsx itself)
          mod = await import(pathToFileURL(indexPath).href)
        }
      } else {
        mod = await import(pathToFileURL(indexPath).href)
      }

      const manifests = extractModuleManifests(mod)

      if (manifests.length > 0) {
        discovered.push(...manifests)
      }
    } catch (err) {
      const errorMsg = (err as Error).message
      logger.warn({ module: entry, err: errorMsg }, 'Module discovery: failed to import')
      // Also log to stderr so CLI users see the error (pino is silenced in CLI mode)
      console.error(`  ⚠ Module '${entry}' failed to load: ${errorMsg}`)
    }
  }

  return discovered
}

// ─── Main API ────────────────────────────────────────────────────────

/**
 * Discover plugins and modules via conventions.
 *
 * Flow:
 * 1. Auto-discover plugins from package.json dependencies
 * 2. Auto-discover modules from src/modules/ directory
 *
 * Results are cached for the lifetime of the process.
 */
export async function loadNexusConfig(): Promise<NexusConfig> {
  if (_cache) return _cache

  const projectPath = getProjectPath()
  const config: NexusConfig = {}

  // 1. Discover plugins from package.json
  const plugins = await discoverPlugins(projectPath)
  if (plugins.length > 0) {
    config.plugins = plugins
  }

  // 2. Discover modules from src/modules/
  const modules = await discoverModules(projectPath)
  if (modules.length > 0) {
    config.modules = modules
  }

  _cache = config
  return _cache
}

/**
 * Reset config cache. Used in tests and server stop() for idempotent restarts.
 */
export function resetConfigCache(): void {
  _cache = null
}
